import { useState, useEffect } from "react";
import { Stage } from "../../../lusca-search-api/generated-src";
import { RedriveExecutionFile, RedriveKind } from "../redriveKinds";
import { RedriveItem } from "../table/redriveItemsTableConfig";

export const useRedriveItemsData = (
  selectedStage: Stage,
  executionId: string | null,
  kind: RedriveKind
) => {
  const [items, setItems] = useState<RedriveItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [isTruncated, setIsTruncated] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [metadata, setMetadata] = useState<RedriveExecutionFile | null>(null);

  function parseContentRange(range: string) {
    const match = range.match(/^bytes (\d+)-(\d+)\/(\d+|\*)$/);
    if (!match) return null;
    const [, start, end, total] = match;
    return {
      start: +start,
      end: +end,
      total: total === "*" ? null : +total,
    };
  }

  function responseIsTruncated(response: Response): boolean {
    const range = response.headers.get("content-range");
    if (!range) return false;
    const parsed = parseContentRange(range);
    if (!parsed?.total) return true;
    return parsed.end + 1 < parsed.total;
  }

  // Fetch the download URL and then fetch the CSV file
  useEffect(() => {
    const fetchItems = async () => {
      if (!executionId) {
        setItems([]);
        setMetadata(null);
        return;
      }

      try {
        setLoading(true);
        setError("");

        const executionFile = await kind.getExecutionFile(
          executionId,
          selectedStage
        );
        setMetadata(executionFile);

        if (executionFile.downloadUrl) {
          setDownloadUrl(executionFile.downloadUrl);
          // Fetch only the first 400KB from the presigned URL
          const csvResponse = await fetch(executionFile.downloadUrl, {
            headers: {
              Range: "bytes=0-409599",
            },
          });
          if (!csvResponse.ok) {
            throw new Error(
              `Failed to download ${kind.name.toLowerCase()}s file`
            );
          }

          const csvText = await csvResponse.text();
          const lines = csvText.split("\n");
          // Check if response was truncated
          const wasTruncated = responseIsTruncated(csvResponse);
          setIsTruncated(wasTruncated);
          // Drop the incomplete last line if response was truncated
          const completeLines = wasTruncated ? lines.slice(0, -1) : lines;

          const itemIds = completeLines
            .map((line) => line.trim())
            .filter((line) => line.length > 0)
            .map((line) => line.split(",")[0].trim()); // Take first column if CSV has multiple columns

          setItems(itemIds.map((itemId) => ({ itemId, stage: selectedStage })));
        } else {
          setItems([]);
          setIsTruncated(false);
          setDownloadUrl(null);
        }
      } catch (error) {
        setError(
          `Failed to load ${kind.name.toLowerCase()}s for this redrive execution: ${
            error instanceof Error ? error.message : String(error)
          }`
        );
        setItems([]);
        setIsTruncated(false);
        setDownloadUrl(null);
        setMetadata(null);
      } finally {
        setLoading(false);
      }
    };

    if (selectedStage && executionId) {
      fetchItems();
    }
  }, [selectedStage, executionId, kind]);

  return { items, loading, error, isTruncated, downloadUrl, metadata };
};
